/**
 * TrampolineSystem - Handles bounce pad mechanics
 * 
 * Creates visual trampolines and handles bounce physics.
 * 
 * @module systems/TrampolineSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';
import { PHYSICS, EFFECTS } from '../configs/GameConfig.js';

export default class TrampolineSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
        this.label = 'trampoline';
    }

    /**
     * Create trampolines from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        if (!config.trampolines) return;

        config.trampolines.forEach(t => {
            // Find Y position from nearest platform
            const nearestPlatform = config.platforms.find(p => 
                t.x >= p.x && t.x <= p.x + p.w
            );
            const trampolineY = nearestPlatform ? nearestPlatform.y - 10 : 700;

            // Physics body (sensor)
            const body = this.scene.matter.add.rectangle(
                t.x + t.w / 2, 
                trampolineY,
                t.w, 
                20, 
                { isStatic: true, isSensor: true, label: this.label }
            );

            // Visual graphics
            const visual = this.scene.add.graphics();
            visual.fillStyle(0xff6600, 1);
            visual.fillRoundedRect(t.x, trampolineY - 10, t.w, 20, 8);
            
            // Spring lines
            visual.lineStyle(3, 0xffcc00);
            for (let i = 0; i < t.w - 20; i += 15) {
                visual.lineBetween(
                    t.x + 10 + i, trampolineY - 5,
                    t.x + 17 + i, trampolineY + 5
                );
            }

            // Bounce arrow indicator
            const arrow = this.scene.add.text(t.x + t.w / 2, trampolineY - 30, '⬆', {
                fontSize: '24px',
                fill: '#ff6600'
            }).setOrigin(0.5);

            this.scene.tweens.add({
                targets: arrow,
                y: trampolineY - 40,
                alpha: 0.5,
                duration: 500,
                yoyo: true,
                repeat: -1
            });

            this.items.push({ 
                body, 
                visual, 
                arrow,
                x: t.x, 
                w: t.w, 
                y: trampolineY 
            });
        });

        this.isActive = this.items.length > 0;
    }

    /**
     * Check if a body belongs to this system
     * @param {Object} body - Physics body
     * @returns {boolean}
     */
    ownsBody(body) {
        return body.label === this.label;
    }

    /**
     * Handle player bouncing on trampoline
     * @param {Object} body - Trampoline body
     * @param {Object} player - Player reference
     * @returns {boolean} True if handled
     */
    handleCollision(body, player) {
        if (!this.ownsBody(body)) return false;
        
        this.bounce(player);
        return true;
    }

    /**
     * Apply bounce effect to player
     * @param {Object} player - Player reference
     */
    bounce(player) {
        if (!player || !player.sprite || !player.sprite.body) return;

        // Strong upward bounce
        this.scene.matter.body.setVelocity(player.sprite.body, {
            x: player.sprite.body.velocity.x,
            y: PHYSICS.TRAMPOLINE.BOUNCE_VELOCITY
        });

        // Visual feedback
        this.scene.cameras.main.shake(
            EFFECTS.SHAKE_DURATION.LIGHT, 
            EFFECTS.SHAKE_INTENSITY.MEDIUM
        );
        
        // Bounce particles
        this.createBounceParticles(player.sprite.x, player.sprite.y + 20);
    }

    /**
     * Create visual bounce effect
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createBounceParticles(x, y) {
        for (let i = 0; i < 8; i++) {
            const angle = Math.PI + (Math.random() - 0.5) * Math.PI * 0.5;
            const particle = this.scene.add.circle(x, y, 6, 0xff6600);
            
            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 50,
                y: y + Math.sin(angle) * 50,
                alpha: 0,
                scale: 0.2,
                duration: 400,
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        this.items.forEach(item => {
            if (item.arrow) item.arrow.destroy();
        });
        super.destroy();
    }
}
